import {
    AlternateEmail as AlternateEmailIcon,
    Business as BusinessIcon,
    Home as HomeIcon,
    ModeEditOutlined as ModeEditOutlinedIcon,
    PhoneIphone as PhoneIphoneIcon,
    Place as PlaceIcon,
    Receipt as ReceiptIcon,
    SwapHorizontalCircle as SwapHorizontalCircleIcon,
} from 'components/icons';
import { Input } from 'components/ui/input';
import { FieldLabel } from 'components/ui/field';
import { Separator } from 'components/ui/separator';
import { Tooltip } from 'components/ui/tooltip';
import { useDebounce } from '@uidotdev/usehooks';
import { FORM_OVERFLOW_LARGE_MODAL_STYLE } from 'components/modals/util.tsx';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getEmployedBy, getEmployees } from 'pages/contacts/contactRelationUtils.ts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useLazySearchContactsQuery,
    useUpdatePartyAddressMutation,
    useUpdatePartyMainEmailMutation,
    useUpdatePartyMainMobileMutation,
} from 'store/api/contacts';
import { AddressDto, ContactDto, ContactMinDtoWithAddresses, PartyType } from 'types/contacts';
import { CustomField } from 'types/customFields.ts';
import { FeatureFlag } from 'types/features.ts';
import { errorAlert, successAlert } from 'utils/alert';
import { getAddress, getClassifierOptionByCode } from 'utils/formUtil.ts';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { lastNameFirstName } from 'utils/nameOrder';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import Loader from '../../../Loader.tsx';
import PermissionButton from '../../../buttons/PermissionButton.tsx';
import EmptyState from 'components/EmptyState';
import EditableTextField from '../../../forms/inputs/EditableTextField.tsx';
import { ModalHeader } from '../../../modals/ModalHeader.tsx';
import AddressModal from '../../../modals/address/AddressModal.tsx';
import AddContactModal from '../../../modals/contact/AddContactModal.tsx';
import { useModal } from '../../../modals/useModal.ts';
import { isValidEmail } from '../../inputs/rhf-validators/validators.ts';
import { PickerModalProps } from './DynamicMultiSelectPicker.tsx';
import { Setting } from 'types/back-office-settings';
import { useSettings } from 'utils/useSettings';

interface AddressRowProps {
    tooltipKey: string;
    icon: React.ReactNode;
    address: AddressDto | null;
    addressType: string;
    isEditable: boolean;
    contactId: number;
    openAddressModal: (
        contactId: number,
        address: AddressDto | null,
        addressType: string,
        sourceAddressForCopy?: AddressDto | null,
        partyType?: 'PERSON' | 'COMPANY'
    ) => void;
    getAddressText: (address: AddressDto) => string;
    sourceAddressForCopy?: AddressDto | null;
    partyType?: 'PERSON' | 'COMPANY';
}

const AddressRow: React.FC<AddressRowProps> = ({
    tooltipKey,
    icon,
    address,
    addressType,
    isEditable,
    contactId,
    openAddressModal,
    getAddressText,
    sourceAddressForCopy,
    partyType,
}) => (
    <Tooltip content={t(tooltipKey)} side="left">
        <div
            className={cn(
                'inline-flex min-h-5 items-start gap-1 text-sm',
                isEditable ? 'cursor-pointer' : 'cursor-default'
            )}
            onClick={(e) => {
                if (isEditable) {
                    e.stopPropagation();
                    openAddressModal(contactId, address, addressType, sourceAddressForCopy, partyType);
                }
            }}
        >
            {icon}
            <div>{address ? getAddressText(address) : '-'}</div>
            {isEditable && <ModeEditOutlinedIcon className="text-grey-500 text-[16px]" />}
        </div>
    </Tooltip>
);

/** Bounds each result table's own scroll container (results arrive 5 at a time, so this shows four
 *  rows and leaves the list scrollable). Without a bound the table grows with its rows and the modal
 *  body becomes the scroller — which would leave the table's infinite scroll with no end to reach. */
const PICKER_TABLE_MAX_HEIGHT = 'max-h-[240px]';

type ContactPickerModalProps = PickerModalProps<ContactMinDtoWithAddresses, ContactDto>;

const ContactPickerModal: React.FC<ContactPickerModalProps> = ({
    onClose,
    onConfirm,
    onCreateNew,
    searchValue,
    isOptionEnabled,
}) => {
    const { openModal, closeModal } = useModal();
    const isCorrespondenceAddressEnabled = useSettings().isSettingEnabled(Setting.CORRESPONDENCE_ADDRESS);
    const lastNameFirst = useIsFeatureEnabled(FeatureFlag.LAST_NAME_FIRST);
    // oxlint-disable-next-line react-doctor/no-derived-useState -- `searchValue` only seeds the search field, which the user then edits on their own
    const [localSearchValue, setLocalSearchValue] = useState(searchValue);
    const isEditingRef = useRef(false);
    const wasEditingRef = useRef(false);
    const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleEditModeChange = useCallback((isEditing: boolean) => {
        if (isEditing) {
            isEditingRef.current = true;
            wasEditingRef.current = true;
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = null;
            }
        } else {
            isEditingRef.current = false;
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
            blurTimeoutRef.current = setTimeout(() => {
                wasEditingRef.current = false;
                blurTimeoutRef.current = null;
            }, 100);
        }
    }, []);

    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    const [updateEmail] = useUpdatePartyMainEmailMutation();
    const [updateMobile] = useUpdatePartyMainMobileMutation();
    const [updateAddress] = useUpdatePartyAddressMutation();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['COUNTRY'],
    });

    // Debounced search value
    const debouncedSearchValue = useDebounce(localSearchValue ?? '', 350);
    const isSearchEnabled = debouncedSearchValue !== null && debouncedSearchValue !== '';

    // Person search
    const [
        fetchPersons,
        { data: personData, isLoading: isPersonLoading, isFetching: isPersonFetching, isError: isPersonError },
    ] = useLazySearchContactsQuery();
    const [personPageIndex, setPersonPageIndex] = useState(0);

    // Company search
    const [
        fetchCompanies,
        { data: companyData, isLoading: isCompanyLoading, isFetching: isCompanyFetching, isError: isCompanyError },
    ] = useLazySearchContactsQuery();
    const [companyPageIndex, setCompanyPageIndex] = useState(0);

    // oxlint-disable-next-line react-doctor/no-effect-event-handler -- driven by the debounced search value, not by an input event: a fetch in the input handler would lose the debounce
    useEffect(() => {
        if (isSearchEnabled) {
            // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- resets the page window for a new search term; the fetch below is what the effect is for
            setPersonPageIndex(0);
            fetchPersons({ query: debouncedSearchValue, partyType: PartyType.PERSON, page: 0, pageSize: 5 });
        }
    }, [debouncedSearchValue, fetchPersons, isSearchEnabled]);

    // oxlint-disable-next-line react-doctor/no-effect-event-handler -- driven by the debounced search value, as the person search above
    useEffect(() => {
        if (isSearchEnabled) {
            // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- resets the page window for a new search term; the fetch below is what the effect is for
            setCompanyPageIndex(0);
            fetchCompanies({ query: debouncedSearchValue, partyType: PartyType.COMPANY, page: 0, pageSize: 5 });
        }
    }, [debouncedSearchValue, fetchCompanies, isSearchEnabled]);

    // Provide interface compatible with previous usePaginatedList usage
    const personList = useMemo(
        () => ({
            dataToDisplay: (isSearchEnabled ? (personData?.content ?? []) : []) as ContactMinDtoWithAddresses[],
            isLoading: isPersonLoading || isPersonFetching,
            handleLoadMore: () => {
                if (!personData?.last) {
                    const nextPage = personPageIndex + 1;
                    fetchPersons({
                        query: debouncedSearchValue,
                        partyType: PartyType.PERSON,
                        page: nextPage,
                        pageSize: 5,
                    });
                    setPersonPageIndex(nextPage);
                }
            },
            hasNextPage: !personData?.last,
        }),
        [
            isSearchEnabled,
            personData,
            isPersonLoading,
            isPersonFetching,
            personPageIndex,
            fetchPersons,
            debouncedSearchValue,
        ]
    );

    const companyList = useMemo(
        () => ({
            dataToDisplay: (isSearchEnabled ? (companyData?.content ?? []) : []) as ContactMinDtoWithAddresses[],
            isLoading: isCompanyLoading || isCompanyFetching,
            handleLoadMore: () => {
                if (!companyData?.last) {
                    const nextPage = companyPageIndex + 1;
                    fetchCompanies({
                        query: debouncedSearchValue,
                        partyType: PartyType.COMPANY,
                        page: nextPage,
                        pageSize: 5,
                    });
                    setCompanyPageIndex(nextPage);
                }
            },
            hasNextPage: !companyData?.last,
        }),
        [
            isSearchEnabled,
            companyData,
            isCompanyLoading,
            isCompanyFetching,
            companyPageIndex,
            fetchCompanies,
            debouncedSearchValue,
        ]
    );

    const validEmail = useCallback((val: unknown) => isValidEmail(val, t('validation.email-must-be-valid')), []);

    const handleUpdateEmail = useCallback(
        async (contactId: number, newEmail: string) => {
            try {
                await updateEmail({ id: contactId, mainEmail: newEmail }).unwrap();
                successAlert(t('contacts.contact-updated'));
            } catch {
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [updateEmail]
    );

    const handleUpdateMobile = useCallback(
        async (contact: { id: number }, newMobile: string) => {
            try {
                await updateMobile({ id: contact.id, mainMobile: newMobile }).unwrap();
                successAlert(t('contacts.contact-updated'));
            } catch {
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [updateMobile]
    );

    const openAddressModal = useCallback(
        (
            contactId: number,
            address: AddressDto | null,
            addressType?: string,
            sourceAddressForCopy?: AddressDto | null,
            partyType?: 'PERSON' | 'COMPANY'
        ) => {
            const isBillingAddress = addressType === 'BILLING';
            openModal(
                <AddressModal
                    onChange={(value) => {
                        void (async () => {
                            try {
                                await updateAddress({ ...value, id: contactId, addressType }).unwrap();
                                successAlert(t('contacts.contact-updated'));
                            } catch {
                                errorAlert(t('forms.error-submitting-the-form'));
                            }
                        })();
                    }}
                    initialData={{
                        additionalInformation: address?.additionalInformation ?? '',
                        address1: address?.address1 ?? '',
                        address2: address?.address2 ?? '',
                        cityName: address?.cityName ?? '',
                        // Unfiltered lookup: this restores the country an existing address already
                        // stores, which has to survive that value no longer being offered.
                        countryCode: getClassifierOptionByCode<string, string>(
                            classifiers['COUNTRY'],
                            address?.countryCode
                        ),
                        zipCode: address?.zipCode ?? '',
                    }}
                    onClose={closeModal}
                    classifiers={classifiers}
                    isBillingAddress={isBillingAddress}
                    sourceAddressForCopy={isBillingAddress ? sourceAddressForCopy : undefined}
                    partyType={partyType}
                    customFields={(address?.customFields as CustomField[]) ?? []}
                />,
                closeModal
            );
        },
        [openModal, classifiers, closeModal, updateAddress]
    );

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSearchValue(event.target.value);
    };

    const handleRowClick = useCallback(
        (contact: ContactMinDtoWithAddresses) => {
            if (isEditingRef.current || wasEditingRef.current) {
                return;
            }
            onConfirm(contact);
        },
        [onConfirm]
    );

    const handleAddNewContact = useCallback(
        (contactType: PartyType) => {
            openModal(
                <AddContactModal
                    onContactAdd={(value: ContactDto) => {
                        onCreateNew(value);
                        onClose();
                    }}
                    onClose={closeModal}
                    contactType={contactType}
                    preSelectedEmployer={undefined}
                    preSelectedEmployee={undefined}
                    initialName={localSearchValue}
                />,
                closeModal,
                FORM_OVERFLOW_LARGE_MODAL_STYLE
            );
        },
        [closeModal, localSearchValue, onClose, onCreateNew, openModal]
    );

    const retryPersonSearch = () => {
        setPersonPageIndex(0);
        fetchPersons({ query: debouncedSearchValue, partyType: PartyType.PERSON, page: 0, pageSize: 5 });
    };
    const retryCompanySearch = () => {
        setCompanyPageIndex(0);
        fetchCompanies({ query: debouncedSearchValue, partyType: PartyType.COMPANY, page: 0, pageSize: 5 });
    };

    const filteredPersonList = useMemo(() => {
        return personList.dataToDisplay.map((person) => ({
            ...person,
            mainEmail: person.mainEmail ?? null,
            mainMobile: person.mainMobile ?? null,
            homeAddress: person.homeAddress ? { ...person.homeAddress } : null,
            isEditable: person.isEditable ?? false,
        }));
    }, [personList.dataToDisplay]);

    const filteredCompanyList = useMemo(() => {
        return companyList.dataToDisplay.map((company) => ({
            ...company,
            mainEmail: company.mainEmail ?? null,
            mainMobile: company.mainMobile ?? null,
            registrationAddress: company.registrationAddress ? { ...company.registrationAddress } : null,
            isEditable: company.isEditable ?? false,
        }));
    }, [companyList.dataToDisplay]);

    const getAddressText = useCallback(
        (address: AddressDto) => {
            const countryLabel =
                classifiers?.['COUNTRY'] && address
                    ? getClassifierText(classifiers?.['COUNTRY'], address?.countryCode)
                    : undefined;
            return getAddress(address as AddressDto, countryLabel);
        },
        [classifiers]
    );

    const personColumns = useMemo<DataTableColumnDef<ContactMinDtoWithAddresses>[]>(
        () => [
            {
                header: t('contacts.picker-modal.name'),
                accessorKey: 'name',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ row, getValue }) => {
                    const value = getValue();
                    return lastNameFirst ? (lastNameFirstName(row.original) ?? value) : value;
                },
            },
            {
                header: t('contacts.picker-modal.official-name'),
                accessorKey: 'officialName',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ getValue }) => getValue(),
            },
            {
                header: t('contacts.picker-modal.person-id'),
                accessorKey: 'personalCode',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ getValue }) => getValue(),
            },
            {
                header: t('contacts.picker-modal.employed-by'),
                id: 'employedBy',
                accessorKey: 'relatesTo',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ getValue }) => {
                    const employedByList = getEmployedBy(getValue() ?? null);
                    const displayItems = employedByList.slice(0, 2);
                    const hasMore = employedByList.length > 2;

                    return (
                        <div className="flex flex-col">
                            <div>{displayItems.map((item) => item.name).join(', ')}</div>
                            {hasMore && <div>…</div>}
                        </div>
                    );
                },
            },
            {
                header: t('contacts.picker-modal.contact-and-home-address'),
                accessorKey: 'mainEmail',
                enableSorting: false,
                meta: {
                    // Stacks the email/phone editors and up to three address rows. Measured in the
                    // picker modal: 186 / 186 / 186 / 186 / 374 px.
                    widthWeight: 2,
                    minPx: 280,
                },
                cell: ({ getValue, row }) => (
                    // Keeps a click on the editors from picking the row; the editors are the controls.
                    <div role="presentation" className="flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <EditableTextField
                            value={getValue()}
                            onSave={async (newValue) => handleUpdateEmail(row.original.id, newValue)}
                            isEditEnabled={row.original.isEditable ?? false}
                            placeholder="-"
                            validation={validEmail}
                            leftIcon={<AlternateEmailIcon className="text-grey-500 text-[16px]" />}
                            onEditModeChange={handleEditModeChange}
                        />
                        <EditableTextField
                            value={row.original.mainMobile}
                            onSave={async (newValue) => handleUpdateMobile(row.original, newValue)}
                            isEditEnabled={row.original.isEditable ?? false}
                            placeholder="-"
                            leftIcon={<PhoneIphoneIcon className="text-grey-500 text-[16px]" />}
                            onEditModeChange={handleEditModeChange}
                        />
                        <Separator className="my-2" />
                        <AddressRow
                            tooltipKey="contacts.picker-modal.address"
                            icon={<HomeIcon className="text-grey-500 mt-[3px] text-[16px]" />}
                            address={row.original.homeAddress ?? null}
                            addressType="HOME"
                            isEditable={row.original.isEditable ?? false}
                            contactId={row.original.id}
                            openAddressModal={openAddressModal}
                            getAddressText={getAddressText}
                        />
                        <AddressRow
                            tooltipKey="contacts.picker-modal.billing-address"
                            icon={<ReceiptIcon className="text-grey-500 mt-[3px] text-[16px]" />}
                            address={row.original.billingAddress ?? null}
                            addressType="BILLING"
                            isEditable={row.original.isEditable ?? false}
                            contactId={row.original.id}
                            openAddressModal={openAddressModal}
                            getAddressText={getAddressText}
                            sourceAddressForCopy={row.original.homeAddress}
                            partyType="PERSON"
                        />
                        {isCorrespondenceAddressEnabled && (
                            <AddressRow
                                tooltipKey="contacts.picker-modal.correspondence-address"
                                icon={<SwapHorizontalCircleIcon className="text-grey-500 mt-[3px] text-[16px]" />}
                                address={row.original.correspondenceAddress ?? null}
                                addressType="CORRESPONDENCE"
                                isEditable={row.original.isEditable ?? false}
                                contactId={row.original.id}
                                openAddressModal={openAddressModal}
                                getAddressText={getAddressText}
                            />
                        )}
                    </div>
                ),
            },
        ],
        [
            validEmail,
            getAddressText,
            handleUpdateEmail,
            handleUpdateMobile,
            openAddressModal,
            isCorrespondenceAddressEnabled,
            handleEditModeChange,
            lastNameFirst,
        ]
    );

    const companyColumns = useMemo<DataTableColumnDef<ContactMinDtoWithAddresses>[]>(
        () => [
            {
                header: t('contacts.picker-modal.name'),
                accessorKey: 'name',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ getValue }) => getValue(),
            },
            {
                header: t('contacts.picker-modal.official-name'),
                accessorKey: 'officialName',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ getValue }) => getValue(),
            },
            {
                header: t('contacts.picker-modal.reg-number'),
                accessorKey: 'regCode',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ getValue }) => getValue(),
            },
            {
                header: t('contacts.picker-modal.employees'),
                id: 'employees',
                accessorKey: 'relations',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: ({ getValue }) => {
                    const employeesList = getEmployees(getValue() ?? null);
                    const displayItems = employeesList.slice(0, 2);
                    const hasMore = employeesList.length > 2;

                    return (
                        <div className="flex flex-col">
                            <div>{displayItems.map((item) => item.name).join(', ')}</div>
                            {hasMore && <div>…</div>}
                        </div>
                    );
                },
            },
            {
                header: t('contacts.picker-modal.contact-and-reg-address'),
                accessorKey: 'mainEmail',
                enableSorting: false,
                meta: {
                    // Four address rows here (registration / visiting / billing / correspondence), so
                    // this column needs more than the person table's: 154 / 154 / 154 / 154 / 503 px.
                    widthWeight: 3.27,
                    minPx: 280,
                },
                cell: ({ getValue, row }) => (
                    // Keeps a click on the editors from picking the row; the editors are the controls.
                    <div role="presentation" className="flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <EditableTextField
                            value={getValue()}
                            onSave={async (newValue) => handleUpdateEmail(row.original.id, newValue)}
                            isEditEnabled={row.original.isEditable ?? false}
                            placeholder="-"
                            validation={validEmail}
                            leftIcon={<AlternateEmailIcon className="text-grey-500 text-[16px]" />}
                            onEditModeChange={handleEditModeChange}
                        />
                        <EditableTextField
                            value={row.original.mainMobile}
                            onSave={async (newValue) => handleUpdateMobile(row.original, newValue)}
                            isEditEnabled={row.original.isEditable ?? false}
                            placeholder="-"
                            leftIcon={<PhoneIphoneIcon className="text-grey-500 text-[16px]" />}
                            onEditModeChange={handleEditModeChange}
                        />
                        <Separator className="my-2" />
                        <AddressRow
                            tooltipKey="contacts.picker-modal.registration-address"
                            icon={<BusinessIcon className="text-grey-500 mt-[3px] text-[16px]" />}
                            address={row.original.registrationAddress ?? null}
                            addressType="REGISTRATION"
                            isEditable={row.original.isEditable ?? false}
                            contactId={row.original.id}
                            openAddressModal={openAddressModal}
                            getAddressText={getAddressText}
                        />
                        <AddressRow
                            tooltipKey="contacts.picker-modal.visiting-address"
                            icon={<PlaceIcon className="text-grey-500 mt-[3px] text-[16px]" />}
                            address={row.original.visitingAddress ?? null}
                            addressType="VISITING"
                            isEditable={row.original.isEditable ?? false}
                            contactId={row.original.id}
                            openAddressModal={openAddressModal}
                            getAddressText={getAddressText}
                        />
                        <AddressRow
                            tooltipKey="contacts.picker-modal.billing-address"
                            icon={<ReceiptIcon className="text-grey-500 mt-[3px] text-[16px]" />}
                            address={row.original.billingAddress ?? null}
                            addressType="BILLING"
                            isEditable={row.original.isEditable ?? false}
                            contactId={row.original.id}
                            openAddressModal={openAddressModal}
                            getAddressText={getAddressText}
                            sourceAddressForCopy={row.original.registrationAddress}
                            partyType="COMPANY"
                        />
                        {isCorrespondenceAddressEnabled && (
                            <AddressRow
                                tooltipKey="contacts.picker-modal.correspondence-address"
                                icon={<SwapHorizontalCircleIcon className="text-grey-500 mt-[3px] text-[16px]" />}
                                address={row.original.correspondenceAddress ?? null}
                                addressType="CORRESPONDENCE"
                                isEditable={row.original.isEditable ?? false}
                                contactId={row.original.id}
                                openAddressModal={openAddressModal}
                                getAddressText={getAddressText}
                            />
                        )}
                    </div>
                ),
            },
        ],
        [
            validEmail,
            getAddressText,
            handleUpdateEmail,
            handleUpdateMobile,
            openAddressModal,
            isCorrespondenceAddressEnabled,
            handleEditModeChange,
        ]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <>
            <ModalHeader title={t('contacts.picker-modal.title')} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-4 py-6 sm:px-8 sm:py-9.5">
                <div className="mb-6">
                    <FieldLabel className="text-fg-secondary mb-1 font-normal" htmlFor="contact-picker-search">
                        {t('contacts.picker-modal.search')}
                    </FieldLabel>
                    <Input
                        id="contact-picker-search"
                        className="bg-paper"
                        value={localSearchValue}
                        onChange={handleSearchChange}
                        placeholder={t('contacts.picker-modal.search')}
                    />
                </div>
                {localSearchValue !== null && localSearchValue !== '' && (
                    <div className="flex flex-col gap-8">
                        <div>
                            <h6 className="mb-4 text-base font-medium">{t('contacts.picker-modal.person')}</h6>
                            {!personList.isLoading && (
                                <PermissionButton
                                    permissions={[
                                        'CREATE.PARTIES',
                                        'CREATE.PARTIES.CLIENTS',
                                        'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                                    ]}
                                    variant="contained"
                                    color="secondary"
                                    size="extraSmall"
                                    onClick={() => handleAddNewContact(PartyType.PERSON)}
                                    className="mb-4"
                                >
                                    <FormattedMessage id="contacts.picker-modal.add-new-contact" />
                                </PermissionButton>
                            )}
                            {((filteredPersonList.length > 0 || personList.isLoading) && (
                                <DataTable<ContactMinDtoWithAddresses>
                                    // Keyed on the SEARCH, not on the loaded rows: `searchContacts`
                                    // merges each page into one cache entry, so a row-derived key
                                    // changes on every page and remounts the table — losing the
                                    // scroll position the next page was requested from. A new search
                                    // is the only thing that should hand the table a fresh scroller.
                                    key={`person-list-${debouncedSearchValue}`}
                                    columns={personColumns}
                                    data={filteredPersonList}
                                    columnConfigId="contact-picker-persons"
                                    isLoading={personList.isLoading}
                                    skeletonRows={2}
                                    onRowClick={handleRowClick}
                                    isRowEnabled={isOptionEnabled}
                                    // Capped so the table — not the modal body — is the scroller the
                                    // next page is requested from; an unbounded container grows with
                                    // its rows and has no end to scroll to.
                                    className={PICKER_TABLE_MAX_HEIGHT}
                                    onLoadMore={personList.handleLoadMore}
                                    hasNextPage={personList.hasNextPage}
                                    isLoadingMore={personList.isLoading}
                                />
                            )) ||
                                // A failed request must not read as "nothing matched" — that sends
                                // the user rewording a search that never reached the server. Gated
                                // on an active search so a stale error does not outlive the request.
                                (isSearchEnabled && isPersonError && (
                                    <EmptyState
                                        size="inline"
                                        variant="error"
                                        onRetry={retryPersonSearch}
                                        surface="contact-picker-persons"
                                        className="border-divider rounded-[10px] border"
                                    />
                                )) ||
                                // A search that matched nothing used to render no message at all —
                                // only the heading and the add button, which reads as still loading.
                                (isSearchEnabled && (
                                    <EmptyState
                                        size="inline"
                                        titleId="contacts.picker-modal.no-people-match"
                                        titleValues={{ search: debouncedSearchValue }}
                                        surface="contact-picker-persons"
                                        className="border-divider rounded-[10px] border"
                                    />
                                ))}
                        </div>
                        <div>
                            <h6 className="mb-4 text-base font-medium">{t('contacts.picker-modal.company')}</h6>
                            {!companyList.isLoading && (
                                <PermissionButton
                                    permissions={[
                                        'CREATE.PARTIES',
                                        'CREATE.PARTIES.CLIENTS',
                                        'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                                    ]}
                                    variant="contained"
                                    color="secondary"
                                    size="extraSmall"
                                    onClick={() => handleAddNewContact(PartyType.COMPANY)}
                                    className="mb-4"
                                >
                                    <FormattedMessage id="contacts.picker-modal.add-new-contact" />
                                </PermissionButton>
                            )}
                            {((filteredCompanyList.length > 0 || companyList.isLoading) && (
                                <DataTable<ContactMinDtoWithAddresses>
                                    key={`company-list-${debouncedSearchValue}`}
                                    columns={companyColumns}
                                    data={filteredCompanyList}
                                    columnConfigId="contact-picker-companies"
                                    isLoading={companyList.isLoading}
                                    skeletonRows={2}
                                    onRowClick={handleRowClick}
                                    isRowEnabled={isOptionEnabled}
                                    className={PICKER_TABLE_MAX_HEIGHT}
                                    onLoadMore={companyList.handleLoadMore}
                                    hasNextPage={companyList.hasNextPage}
                                    isLoadingMore={companyList.isLoading}
                                />
                            )) ||
                                // Company section: only the COMPANY query's error belongs here — the
                                // person query's failure must not short-circuit this section's own
                                // no-match fallback.
                                (isSearchEnabled && isCompanyError && (
                                    <EmptyState
                                        size="inline"
                                        variant="error"
                                        onRetry={retryCompanySearch}
                                        surface="contact-picker-companies"
                                        className="border-divider rounded-[10px] border"
                                    />
                                )) ||
                                (isSearchEnabled && (
                                    <EmptyState
                                        size="inline"
                                        titleId="contacts.picker-modal.no-companies-match"
                                        titleValues={{ search: debouncedSearchValue }}
                                        surface="contact-picker-companies"
                                        className="border-divider rounded-[10px] border"
                                    />
                                ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ContactPickerModal;
